import { I18n } from "@assets/strings";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { allCategories, Category, subCategoriesByCategory } from "@domain/lms/category/category";
import { gap } from "@ui/styles/utils";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CategoryIcon, ChevronDownIcon } from "@ui/components/icons";
import { Menu } from "@ui/components/menus/menu";
import { CategoryEditorProps } from "./categoryEditorProps";

export const DesktopCategoryEditor: React.FC<CategoryEditorProps> = ({
	category,
	subCategory,
	setCategory,
	setSubCategory,
	label,
	position = "bottom",
	menuHeight = undefined,
	showShevron = true,
	inputStyle = undefined,
	...htmlProps
}) => {
	const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
	const hasSubs = (cat: Category): boolean => subCategoriesByCategory[cat].length > 0;
	const hoverTimeout = useRef<NodeJS.Timeout>(null);
	const xVelocity = useRef(0);

	useEffect(() => {
		const updateVelocity = (e: MouseEvent): void => {
			xVelocity.current = e.movementX;
		};
		document.addEventListener("mousemove", updateVelocity);
		return () => document.removeEventListener("mousemove", updateVelocity);
	}, []);

	return (
		<Menu
			{...htmlProps}
			align="left"
			menuHeight={menuHeight}
			position={position}
			onOpenChange={(opened) => !opened && setSelectedCategory(null)}
			renderMenuContent={(close) => (
				<div>
					<MenuContent>
						{allCategories.map((cat) => (
							<CategoryRow
								selected={selectedCategory === cat}
								key={cat}
								onClick={() => {
									if (hasSubs(cat) && !!setSubCategory) {
										setSelectedCategory(cat);
									} else {
										close();
										setCategory(cat);
										setSubCategory?.();
									}
								}}
								onMouseEnter={() => {
									if (xVelocity.current > 0) {
										hoverTimeout.current = setTimeout(() => {
											setSelectedCategory(cat);
										}, 200);
									} else {
										setSelectedCategory(cat);
									}
								}}
								onMouseLeave={() => {
									if (hoverTimeout.current) {
										clearTimeout(hoverTimeout.current);
									}
								}}
							>
								{I18n.t(`category.${cat}`)}
							</CategoryRow>
						))}
					</MenuContent>
					{selectedCategory && !!setSubCategory && hasSubs(selectedCategory) && (
						<SubMenu>
							<SubMenuHeader onClick={() => setSelectedCategory(null)}>
								{showShevron && <ChevronDownIcon />}
								{I18n.t(`category.${selectedCategory}`)}
							</SubMenuHeader>
							{subCategoriesByCategory[selectedCategory].map((subCat) => (
								<CategoryRow
									key={subCat}
									onClick={() => {
										setCategory(selectedCategory);
										setSubCategory(subCat);
										close();
									}}
								>
									{I18n.t(`category.${subCat}`)}
								</CategoryRow>
							))}
						</SubMenu>
					)}
				</div>
			)}
			menuWrapperStyle={{ paddingTop: 10, paddingBottom: 150 }}
		>
			<Container>
				<CategoryIcon />
				<CategoryPicker style={inputStyle}>
					{category ? I18n.t(subCategory ? `category.${subCategory}` : `category.${category}`) : label + "*"}
					{showShevron && <ChevronDownIcon />}
				</CategoryPicker>
			</Container>
		</Menu>
	);
};

const Container = styled(RoundedBlock)`
	padding: 10px 16px;
	display: flex;
	align-items: center;
	cursor: pointer;

	> svg:first-child {
		width: 20px;
		height: 20px;
		margin-right: 10px;
	}
`;

const CategoryPicker = styled.div`
	${({ theme }) => theme.textStyles.primary};
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const MenuContent = styled.div`
	${gap(18)}
`;

const SubMenu = styled(RoundedBlock)`
	position: absolute;
	right: 0;
	top: 10px;
	bottom: 0;
	padding: 12px 14px;
	white-space: initial;
	overflow: hidden;
	${gap(18)}
	transform: translateX(100%);
	right: -5px;
	bottom: initial;
`;

const SubMenuHeader = styled.div`
	display: flex;
	align-items: center;
	font-size: 0.75rem;
	font-weight: bold;
	color: ${({ theme }) => theme.colors.primary};
	cursor: pointer;
	> svg {
		margin-right: 5px;
		width: 12px;
		transform: rotate(90deg);
		.main-path {
			fill: ${({ theme }) => theme.colors.primary};
		}
	}
	margin-bottom: 10px;
`;

const CategoryRow = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	${({ theme }) => theme.textStyles.primary};
	${({ theme, selected }) => selected && `color: ${theme.colors.primary}`};
	font-size: 0.75rem;
	display: flex;
	cursor: pointer;
	transition: color 0.1s linear;
	&:hover {
		color: ${({ theme }) => theme.colors.primary};
	}
`;
